import type { MuseumContent } from "./contracts";
import { languageOptionsHtml, type Language, type UiCopy } from "./i18n";

export type ViewerUI = ReturnType<typeof createViewerUI>;

export function createViewerUI(root: HTMLElement, language: Language, copy: UiCopy) {
  const languageSelector = `
    <label class="language-picker">
      <span>${copy.languageLabel}</span>
      <select data-language aria-label="${copy.languageLabel}">
        ${languageOptionsHtml(language)}
      </select>
    </label>`;
  root.innerHTML = `
    <main class="viewer" aria-labelledby="exhibition-title">
      <canvas class="museum-canvas" tabindex="-1" aria-label="${copy.canvasLabel}"></canvas>
      <section class="welcome panel" data-welcome aria-describedby="exhibition-thesis">
        <p class="eyebrow">${copy.museumEyebrow}</p>
        <h1 id="exhibition-title">${copy.fallbackTitle}</h1>
        <p id="exhibition-thesis">${copy.loadingThesis}</p>
        <p class="load-status" role="status" aria-live="polite" data-load-status>${copy.preparing}</p>
        <!-- Five real validation stages, not a decorative bar: each segment fills when
             that check has actually passed, so the visitor is watching work, not a timer. -->
        <div class="load-track" data-load-track aria-hidden="true">
          ${["manifest", "report", "content", "scene", "validated"]
            .map((phase) => `<span class="load-track__step" data-phase="${phase}"></span>`)
            .join("")}
        </div>
        <div class="welcome-actions">
          <button class="primary" type="button" data-enter disabled>${copy.enter}</button>
          <!-- Offered at the door, beside walking in. A visitor who does not know the
               collection has no way to choose where to go first, and the guided route
               is the answer to that — it should not be buried in the toolbar. -->
          <button type="button" data-welcome-tour disabled>${copy.playTour}</button>
          <button type="button" data-back-exhibitions>${copy.backToExhibitions}</button>
          <button type="button" data-open-route>${copy.routeAndWorks}</button>
          <button type="button" data-open-help>${copy.howToVisit}</button>
          ${languageSelector}
        </div>
      </section>
      <section class="pause panel" data-pause hidden>
        <p class="eyebrow">${copy.pauseEyebrow}</p>
        <h2>${copy.pauseTitle}</h2>
        <p>${copy.pauseMessage}</p>
        <button class="primary" type="button" data-continue>${copy.continueVisit}</button>
      </section>
      <section class="fatal panel" role="alert" data-fatal hidden>
        <p class="eyebrow">${copy.fatalEyebrow}</p>
        <h2 data-fatal-title>${copy.fatalTitle}</h2>
        <p data-fatal-message></p>
        <button class="primary" type="button" data-retry>${copy.retry}</button>
        <button type="button" data-back-exhibitions>${copy.backToExhibitions}</button>
        <button type="button" data-fatal-route hidden>${copy.consultRoute}</button>
      </section>
      <header class="hud" data-hud hidden>
        <div class="hud-place">
          <span data-room></span>
          <strong data-route-title></strong>
          <span data-route-progress>1/1</span>
        </div>
        <div class="guidance" aria-hidden="true">
          <span class="guidance-arrow" data-guidance-arrow>↑</span>
          <span data-guidance-label></span>
          <span data-guidance-distance></span>
        </div>
        <nav aria-label="${copy.visitControls}">
          <button type="button" data-back-exhibitions>${copy.backToExhibitions}</button>
          <button type="button" data-previous aria-label="${copy.previousArtwork}">←</button>
          <button type="button" data-next aria-label="${copy.nextArtwork}">→</button>
          <button type="button" data-tour>${copy.playTour}</button>
          <button type="button" data-hud-route>${copy.works}</button>
          <button type="button" data-reset>${copy.home}</button>
          <button type="button" data-hud-help>${copy.help}</button>
          ${languageSelector}
        </nav>
      </header>
      <div class="reticle" aria-hidden="true" data-reticle hidden><span></span></div>
      <!-- The tour's own caption strip. It sits low and wide like a subtitle so the
           frame above it stays the picture, and it carries the only control a visitor
           needs while the camera is driving: the one that gives them the camera back. -->
      <div class="tour-bar" data-tour-bar hidden>
        <p class="tour-bar__count" data-tour-count></p>
        <p class="tour-bar__title" data-tour-title></p>
        <button type="button" data-tour-stop>${copy.stopTour}</button>
      </div>
      <p class="sr-only" role="status" aria-live="polite" data-selection hidden></p>
      <div class="touch-controls" data-touch-controls hidden>
        <div class="joystick" data-joystick aria-label="${copy.joystickLabel}"><span data-joystick-knob></span></div>
        <button class="touch-interact" type="button" data-touch-interact disabled>${copy.viewArtwork}</button>
      </div>
      <details class="minimap" data-minimap hidden open>
        <summary><span>${copy.mapTitle}</span><kbd>M</kbd></summary>
        <canvas width="520" height="240" data-minimap-canvas aria-label="${copy.mapLabel}"></canvas>
        <label class="sr-only" for="navigation-preset">${copy.presetLabel}</label>
        <select id="navigation-preset" data-navigation-preset>
          <option value="">${copy.presetPlaceholder}</option>
        </select>
        <small>${copy.presetHint}</small>
      </details>
      <div class="sr-only" aria-live="polite" data-announcer></div>
    </main>
    <dialog class="artwork-dialog" data-artwork-dialog aria-labelledby="artwork-dialog-title">
      <article>
        <figure class="artwork-dialog-visual">
          <canvas data-artwork-image role="button" tabindex="0" aria-label=""></canvas>
        </figure>
        <section class="artwork-dialog-content" data-artwork-dialog-content>
          <header>
            <p class="eyebrow" data-artwork-kicker></p>
            <h2 id="artwork-dialog-title" data-artwork-title></h2>
            <button class="dialog-close" type="button" data-close-artwork aria-label="${copy.closeArtwork}">×</button>
            <dl class="artwork-metadata" data-artwork-metadata></dl>
          </header>
          <div class="review" data-artwork-review></div>
        </section>
      </article>
    </dialog>
    <dialog class="route-dialog" data-route-dialog aria-labelledby="route-dialog-title">
      <header>
        <p class="eyebrow">${copy.routeEyebrow}</p>
        <h2 id="route-dialog-title">${copy.routeTitle}</h2>
        <button class="dialog-close" type="button" data-close-route aria-label="${copy.closeRoute}">×</button>
      </header>
      <p>${copy.routeIntro}</p>
      <ol class="route-list" data-route-list></ol>
    </dialog>
    <dialog class="help-dialog" data-help-dialog aria-labelledby="help-dialog-title">
      <header>
        <p class="eyebrow">${copy.howToVisit}</p>
        <h2 id="help-dialog-title">${copy.helpTitle}</h2>
        <button class="dialog-close" type="button" data-close-help aria-label="${copy.closeHelp}">×</button>
      </header>
      <div class="help-grid">
        <section><h3>${copy.desktopTitle}</h3><p>${copy.desktopHelp}</p></section>
        <section><h3>${copy.touchTitle}</h3><p>${copy.touchHelp}</p></section>
        <section><h3>${copy.tourTitle}</h3><p>${copy.tourHelp}</p></section>
      </div>
    </dialog>
  `;

  const canvas = get<HTMLCanvasElement>(root, ".museum-canvas");
  const artworkDialog = get<HTMLDialogElement>(root, "[data-artwork-dialog]");
  const routeDialog = get<HTMLDialogElement>(root, "[data-route-dialog]");
  const helpDialog = get<HTMLDialogElement>(root, "[data-help-dialog]");
  const isTouch = window.matchMedia("(pointer: coarse)").matches;
  const minimap = get<HTMLDetailsElement>(root, "[data-minimap]");
  minimap.open = !isTouch;
  return {
    root,
    canvas,
    welcome: get<HTMLElement>(root, "[data-welcome]"),
    pause: get<HTMLElement>(root, "[data-pause]"),
    fatal: get<HTMLElement>(root, "[data-fatal]"),
    fatalMessage: get<HTMLElement>(root, "[data-fatal-message]"),
    fatalRoute: get<HTMLButtonElement>(root, "[data-fatal-route]"),
    loadStatus: get<HTMLElement>(root, "[data-load-status]"),
    loadTrack: get<HTMLElement>(root, "[data-load-track]"),
    exhibitionTitle: get<HTMLElement>(root, "#exhibition-title"),
    exhibitionThesis: get<HTMLElement>(root, "#exhibition-thesis"),
    enter: get<HTMLButtonElement>(root, "[data-enter]"),
    continueButton: get<HTMLButtonElement>(root, "[data-continue]"),
    retry: get<HTMLButtonElement>(root, "[data-retry]"),
    hud: get<HTMLElement>(root, "[data-hud]"),
    room: get<HTMLElement>(root, "[data-room]"),
    routeTitle: get<HTMLElement>(root, "[data-route-title]"),
    routeProgress: get<HTMLElement>(root, "[data-route-progress]"),
    guidanceArrow: get<HTMLElement>(root, "[data-guidance-arrow]"),
    guidanceLabel: get<HTMLElement>(root, "[data-guidance-label]"),
    guidanceDistance: get<HTMLElement>(root, "[data-guidance-distance]"),
    reticle: get<HTMLElement>(root, "[data-reticle]"),
    tour: get<HTMLButtonElement>(root, "[data-tour]"),
    welcomeTour: get<HTMLButtonElement>(root, "[data-welcome-tour]"),
    tourBar: get<HTMLElement>(root, "[data-tour-bar]"),
    tourCount: get<HTMLElement>(root, "[data-tour-count]"),
    tourStopButton: get<HTMLButtonElement>(root, "[data-tour-stop]"),
    tourStopTitle: get<HTMLElement>(root, "[data-tour-title]"),
    selection: get<HTMLElement>(root, "[data-selection]"),
    announcer: get<HTMLElement>(root, "[data-announcer]"),
    previous: get<HTMLButtonElement>(root, "[data-previous]"),
    next: get<HTMLButtonElement>(root, "[data-next]"),
    reset: get<HTMLButtonElement>(root, "[data-reset]"),
    routeButtons: [
      get<HTMLButtonElement>(root, "[data-open-route]"),
      get<HTMLButtonElement>(root, "[data-hud-route]"),
      get<HTMLButtonElement>(root, "[data-fatal-route]"),
    ],
    helpButtons: [
      get<HTMLButtonElement>(root, "[data-open-help]"),
      get<HTMLButtonElement>(root, "[data-hud-help]"),
    ],
    artworkDialog,
    routeDialog,
    routeList: get<HTMLOListElement>(root, "[data-route-list]"),
    helpDialog,
    touchControls: get<HTMLElement>(root, "[data-touch-controls]"),
    joystick: get<HTMLElement>(root, "[data-joystick]"),
    joystickKnob: get<HTMLElement>(root, "[data-joystick-knob]"),
    touchInteract: get<HTMLButtonElement>(root, "[data-touch-interact]"),
    minimap,
    minimapCanvas: get<HTMLCanvasElement>(root, "[data-minimap-canvas]"),
    navigationPreset: get<HTMLSelectElement>(root, "[data-navigation-preset]"),
    languageSelectors: Array.from(root.querySelectorAll<HTMLSelectElement>("[data-language]")),
    backButtons: Array.from(root.querySelectorAll<HTMLButtonElement>("[data-back-exhibitions]")),
    isTouch,
  };
}

export function populateNavigationPresets(ui: ViewerUI, content: MuseumContent): void {
  for (const artwork of content.artworks) {
    const option = document.createElement("option");
    option.value = artwork.id;
    option.textContent = `${String(artwork.route_order).padStart(2, "0")} · ${artwork.title}`;
    ui.navigationPreset.append(option);
  }
}

export function populateRouteList(
  ui: ViewerUI,
  content: MuseumContent,
  visited: ReadonlySet<string>,
  onOpen: (artworkId: string, button: HTMLButtonElement) => void,
): void {
  ui.routeList.replaceChildren();
  let roomId = "";
  content.artworks.forEach((artwork) => {
    const item = document.createElement("li");
    if (artwork.room_id !== roomId) {
      roomId = artwork.room_id;
      const room = content.rooms.find((candidate) => candidate.id === roomId);
      const heading = document.createElement("h3");
      heading.textContent = room?.title ?? roomId;
      item.append(heading);
    }
    const button = document.createElement("button");
    button.type = "button";
    button.dataset.artworkId = artwork.id;
    button.className = visited.has(artwork.id) ? "visited" : "";
    const order = document.createElement("span");
    order.textContent = String(artwork.route_order).padStart(2, "0");
    const label = document.createElement("span");
    const title = document.createElement("strong");
    const author = document.createElement("small");
    title.textContent = artwork.title;
    author.textContent = artwork.author;
    label.append(title, author);
    button.append(order, label);
    button.addEventListener("click", () => onOpen(artwork.id, button));
    item.append(button);
    ui.routeList.append(item);
  });
}

function get<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (element === null) throw new Error(`UI_ELEMENT_MISSING: ${selector}`);
  return element;
}
